import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { apiParameters } from 'src/app/configs/api.params';
import { IFeatureModel } from '../models/feature.model';


@Injectable({
  providedIn: 'root'
})
export class FeatureService {

  /**
   * List of features
   */
  private features = [
    {
      "name": 'licensePlateImage',
      "icon": '/assets/img/plates-icon.svg'
    },
    {
      "name": 'reflectiveFloor',
      "icon": '/assets/img/reflection-icon.svg'
    },
    {
      "name": 'cutting',
      "icon": '/assets/img/crop-car-icon.svg'
    },
    {
      "name": 'polishing',
      "icon": '/assets/img/car-paint-icon.svg'
    },
    {
      "name": 'rimPolishing',
      "icon": '/assets/img/rim-icon.svg'
    },
    {
      "name": 'licensePlateBlur',
      "icon": '/assets/img/plates-blur.svg'
    },
    {
      "name": 'flipImage',
      "icon": '/assets/img/flip-icon.svg'
    },
    {
      "name": 'platform_360',
      "icon": '/assets/img/360.svg'
    },
    {
      "name": 'removeReflections',
      "icon": '/assets/img/removeReflections.svg'
    },
    {
      "name": 'refectiveFloorFull',
      "icon": '/assets/img/car-reflection.svg'
    },
    {
      "name": 'panorama_front',
      "icon": '/assets/img/panorama-front.svg'
    },
    {
      "name": 'panorama_back',
      "icon": '/assets/img/panorama-back.svg'
    },
    {
      "name": 'markers',
      "icon": '/assets/img/marker_icon.svg'
    },
    {
      "name": 'carconfImport',
      "icon": '/assets/img/car-confi-import.svg'
    },
    {
      "name": 'instagramCatch',
      "icon": '/assets/img/instagram-catch.svg'
    },
    {
      "name": 'instagram',
      "icon": '/assets/img/instagram.svg'
    },
    {
      "name": 'pro',
      "icon": '/assets/img/pro-icon.svg'
    },
    {
      name: 'premium360',
      icon: '/assets/img/360-video.svg',
    },
    {
      name: 'premiumVideo',
      icon: '/assets/img/image-video.svg',
    },
  ]

  constructor(
    private http: HttpClient) { }


  /**
   * Adds feature
   * @param body - feature data e.g. { name:'Flip',displayName:'Flip image', description:'feature description',
   * price:'100', type:'General', dependencies[]:'Floor reflection', conflicts[]:'segmetation', file:'image.jpg'}
   * @returns feature 
   */
  addFeature(body): Observable<IFeatureModel> {
    return this.http.post(apiParameters.url + '/features', body)
      .pipe(map((res: IFeatureModel) => {
        return res;
      }));
  }

  /**
   * Gets features
   * @returns List of features 
   */
  getFeatures(filterOptions: { includeTypes?: string[], excludeNames?: string[] } = {}): Observable<IFeatureModel[]> {
    return this.http.get(apiParameters.url + '/features')
      .pipe(
        map((res: IFeatureModel[]) => {
          return this.filterFeatures(res, filterOptions);
        }),
        map((res: IFeatureModel[]) => {
          for (const a of res) {
            for (const f of this.features) {
              if (a.name === f.name) {
                Object.assign(a, f);
              }
            }
          }
          return res;
        }),
        map((res: IFeatureModel[]) => {
          res.sort((a, b) => {
            return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
          });
          return res;
        })
      );
  }

  filterFeatures(features: IFeatureModel[], filterOptions: { includeTypes?: string[], excludeNames?: string[] }) {
    return features.filter(feature => {
      const matchesType = filterOptions.includeTypes ? filterOptions.includeTypes.includes(feature.type) : true;
      const matchesExcludeNames = filterOptions.excludeNames ? !filterOptions.excludeNames.includes(feature.name) : true;
      
      return matchesType && matchesExcludeNames;
    });
  }

  getAllFeatures(): Observable<IFeatureModel[]> {
    return this.http.get(apiParameters.url + '/features')
    .pipe(
      map((res: IFeatureModel[]) => {
        res.sort((a, b) => {
          return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
        });
        return res;
      })
    );
  }

  getById(featureId: string): Observable<IFeatureModel> {
    return this.http.get(apiParameters.url + '/features/' + featureId)
      .pipe(map((res: IFeatureModel) => {
        return res;
      }));
  }

  deleteFeature(featureId: string): Observable<any> {
    return this.http.delete(apiParameters.url + '/features/' + featureId);
  }

  updateFeature(data, featureId: string): Observable<IFeatureModel> {
    return this.http.patch(`${apiParameters.url}/features/${featureId}`, data);
  }

  addPriceForClient(clientId: string, body): Observable<IFeatureModel> {
    return this.http.post(`${apiParameters.url}/clients/${clientId}/features`, body)
      .pipe(map((res: IFeatureModel) => {
        return res;
      }));
  }

  getClientFeatures(clientId: string): Observable<IFeatureModel[]> {
    return this.http.get(`${apiParameters.url}/clients/${clientId}/features`)
      .pipe(map((res: IFeatureModel[]) => {
        res.sort((a, b) => {
          return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
        });
        return res;
      }));
  }

  getCustomerFeatures(customerId: string): Observable<IFeatureModel[]> {
    return this.http.get(`${apiParameters.url}/customers/${customerId}/features`)
      .pipe(map((res: IFeatureModel[]) => {
        res.sort((a, b) => {
          return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
        });
        return res;
      }));
  }

  addPriceForCustomer(customerId: string, body): Observable<IFeatureModel> {
    return this.http.post(`${apiParameters.url}/customers/${customerId}/features`, body)
      .pipe(map((res: IFeatureModel) => {
        return res;
      }));
  }

  updatePriceForClient(clientId: string, clientFeatureId: string, body): Observable<IFeatureModel> {
    return this.http.patch(`${apiParameters.url}/clients/${clientId}/features/${clientFeatureId}`, body)
      .pipe(map((res: IFeatureModel) => {
        return res;
      }));
  }

  updatePriceForCustomer(customerId: string, customerFeatureId: string, body): Observable<IFeatureModel> {
    return this.http.patch(`${apiParameters.url}/customers/${customerId}/features/${customerFeatureId}`, body)
      .pipe(map((res: IFeatureModel) => {
        return res;
      }));
  }

  deleteClientPrice(clientId: string, featureId: string): Observable<any> {
    return this.http.delete(`${apiParameters.url}/clients/${clientId}/features/${featureId}`);
  }


  deleteCustomerPrice(customerId: string, featureId: string): Observable<any> {
    return this.http.delete(`${apiParameters.url}/customers/${customerId}/features/${featureId}`);
  }

}
